/**
 * /account 帳號設定（四角色；2026-08-21 帳號管理批次）
 * 分頁：個人資料（姓名／語言／合作方聯絡資訊）、變更密碼。
 */
import { useEffect, useState } from "react";
import { UserRound, KeyRound, Save } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ChangePasswordForm } from "../../components/account/ChangePasswordForm";
import { FormNotice } from "../../components/account/FormNotice";
import { REGION_LOC } from "../admin/base-data-shared";
import { fmtDateTime } from "../../lib/format";
import { SUPPORTED_LANGUAGES, type Language } from "../../../../shared/const";

const LANG_NAME: Record<Language, string> = { "zh-TW": "繁體中文", "zh-CN": "简体中文", "en-US": "English" };

export default function AccountSettings() {
  const { t, lang, setLang } = useLanguage();
  const { refetch: refetchAuth } = useAuth();
  const meQ = trpc.account.me.useQuery();
  const me = meQ.data;

  const [name, setName] = useState("");
  const [language, setLanguage] = useState<Language>(lang);
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!me) return;
    setName(me.name);
    setLanguage(me.language);
    setContactPerson(me.party?.contactPerson ?? "");
    setPhone(me.party?.phone ?? "");
  }, [me]);

  const save = trpc.account.updateProfile.useMutation({
    onSuccess: () => {
      setSaved(true);
      setLang(language);
      void meQ.refetch();
      refetchAuth();
    },
    onError: (e) => setError(e.message),
  });

  const dirty =
    !!me &&
    (name.trim() !== me.name ||
      language !== me.language ||
      (me.party !== null && (contactPerson.trim() !== (me.party.contactPerson ?? "") || phone.trim() !== (me.party.phone ?? ""))));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "帳號設定", "en-US": "Account settings", "zh-CN": "帐号设定" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({ "zh-TW": "管理你的個人資料、介面語言與登入密碼。", "en-US": "Manage your profile, language and password.", "zh-CN": "管理你的个人资料、介面语言与登入密码。" })}
        </p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">
            <UserRound className="mr-1 h-3.5 w-3.5" />
            {t({ "zh-TW": "個人資料", "en-US": "Profile", "zh-CN": "个人资料" })}
          </TabsTrigger>
          <TabsTrigger value="password">
            <KeyRound className="mr-1 h-3.5 w-3.5" />
            {t({ "zh-TW": "變更密碼", "en-US": "Password", "zh-CN": "变更密码" })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>{t({ "zh-TW": "個人資料", "en-US": "Profile", "zh-CN": "个人资料" })}</CardTitle>
              <CardDescription>
                {t({
                  "zh-TW": "Email 與公司資料由平台管理，如需變更請聯絡平台。",
                  "en-US": "Email and company details are managed by the platform — contact us to change them.",
                  "zh-CN": "Email 与公司资料由平台管理，如需变更请联系平台。",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!me ? (
                <p className="text-[13px] text-muted-foreground">{t({ "zh-TW": "載入中…", "en-US": "Loading…", "zh-CN": "载入中…" })}</p>
              ) : (
                <form
                  className="space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setError(null);
                    setSaved(false);
                    save.mutate({
                      name: name.trim(),
                      language,
                      ...(me.party ? { contactPerson: contactPerson.trim(), phone: phone.trim() } : {}),
                    });
                  }}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-[12px]">Email</Label>
                      <Input value={me.email} readOnly disabled />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[12px]">{t({ "zh-TW": "角色", "en-US": "Role", "zh-CN": "角色" })}</Label>
                      <div className="flex h-9 items-center gap-2">
                        <Badge variant="primary">{me.role}</Badge>
                        {me.isOwner === "yes" && <Badge variant="warm" size="sm">owner</Badge>}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="acct-name" className="text-[12px]">{t({ "zh-TW": "姓名", "en-US": "Name", "zh-CN": "姓名" })} *</Label>
                      <Input id="acct-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={128} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="acct-lang" className="text-[12px]">{t({ "zh-TW": "介面語言", "en-US": "Language", "zh-CN": "介面语言" })}</Label>
                      <Select id="acct-lang" value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
                        {SUPPORTED_LANGUAGES.map((l) => (
                          <option key={l} value={l}>{LANG_NAME[l]}</option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  {me.party && (
                    <div className="space-y-4 rounded-md border border-border bg-muted/40 p-4">
                      <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                        {me.party.type === "agency"
                          ? t({ "zh-TW": "旅行社資料", "en-US": "Agency", "zh-CN": "旅行社资料" })
                          : t({ "zh-TW": "車隊資料", "en-US": "Fleet", "zh-CN": "车队资料" })}
                      </p>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-[12px]">{t({ "zh-TW": "公司名稱", "en-US": "Company", "zh-CN": "公司名称" })}</Label>
                          <Input value={me.party.companyName} readOnly disabled />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[12px]">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
                          <Input value={t(REGION_LOC[me.party.region])} readOnly disabled />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="acct-contact" className="text-[12px]">{t({ "zh-TW": "聯絡人", "en-US": "Contact person", "zh-CN": "联络人" })}</Label>
                          <Input id="acct-contact" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} maxLength={128} />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="acct-phone" className="text-[12px]">{t({ "zh-TW": "聯絡電話", "en-US": "Phone", "zh-CN": "联络电话" })}</Label>
                          <Input id="acct-phone" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={64} />
                        </div>
                      </div>
                    </div>
                  )}

                  {error && <FormNotice kind="error">{error}</FormNotice>}
                  {saved && !dirty && (
                    <FormNotice kind="success">{t({ "zh-TW": "已儲存。", "en-US": "Saved.", "zh-CN": "已储存。" })}</FormNotice>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[12px] text-muted-foreground">
                      {t({ "zh-TW": "上次登入", "en-US": "Last sign-in", "zh-CN": "上次登入" })}：{fmtDateTime(me.lastSignedInAt, lang)}
                      {me.passwordChangedAt && (
                        <> · {t({ "zh-TW": "密碼更新於", "en-US": "Password changed", "zh-CN": "密码更新于" })} {fmtDateTime(me.passwordChangedAt, lang)}</>
                      )}
                    </p>
                    <Button type="submit" disabled={!dirty || name.trim() === "" || save.isPending}>
                      <Save className="h-3.5 w-3.5" />
                      {save.isPending ? t({ "zh-TW": "儲存中…", "en-US": "Saving…", "zh-CN": "储存中…" }) : t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "储存" })}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>{t({ "zh-TW": "變更密碼", "en-US": "Change password", "zh-CN": "变更密码" })}</CardTitle>
              <CardDescription>
                {t({
                  "zh-TW": "變更後，其他裝置上的登入會立即失效。",
                  "en-US": "After changing, sessions on other devices are signed out immediately.",
                  "zh-CN": "变更后，其他装置上的登入会立即失效。",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm">
                <ChangePasswordForm onSuccess={() => refetchAuth()} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
